import dayjs from "dayjs";
import { api } from "@/helper/api";

export const COURSE_ADMIN_PERMISSION = "frontend_view_course_administration";
export const COURSE_ACCESS_PERMISSION = "frontend_view_course";
export const COURSE_CREATE_PERMISSION = "add_course";
export const COURSE_UPDATE_PERMISSION = "change_course";
export const COURSE_VARIANT_UPDATE_PERMISSION = "change_courselanguagevariant";
export const COURSE_DELETE_PERMISSION = "delete_course";
export const LMS_BILLING_PERMISSION = "billing_enrolment";
export const COURSE_FEATURED_IMAGE_UPLOAD_PERMISSION =
  "upload_featured_image_courselanguagevariant";
export const COURSE_SCORM_UPLOAD_PERMISSION =
  "upload_scorm_file_courselanguagevariant";
export type LmsMode = "learner" | "administrator" | "manager";

interface GetEffectiveLmsModeProps {
  subdomain: string;
  permissions: string[];
}

export const getEffectiveLmsMode = ({
  subdomain,
  permissions,
}: GetEffectiveLmsModeProps): LmsMode => {
  const canViewAdministratorMode =
    subdomain === "admin" && permissions.includes(COURSE_ADMIN_PERMISSION);

  if (subdomain === "manage") {
    return "manager";
  }

  return canViewAdministratorMode ? "administrator" : "learner";
};

export const shouldBlockLmsForSubdomain = ({
  subdomain,
  permissions,
}: GetEffectiveLmsModeProps) =>
  !permissions.includes(
    subdomain === "admin" ? COURSE_ADMIN_PERMISSION : COURSE_ACCESS_PERMISSION
  );

export const getLmsCoursesRoute = (mode: LmsMode) =>
  mode === "administrator"
    ? "/kaerdemy/administrator/courses"
    : mode === "manager"
    ? "/kaerdemy/manager/courses"
    : "/kaerdemy/learner/courses";

export const getLmsCourseOverviewRoute = (mode: LmsMode, courseId: string) =>
  `${getLmsCoursesRoute(mode)}/${courseId}/overview`;

export const getLmsLearnerEnrolmentOverviewRoute = (
  enrolmentId: string,
  language?: string
) => {
  const baseRoute = `/kaerdemy/learner/enrolments/${enrolmentId}/overview`;
  return language
    ? `${baseRoute}?language=${encodeURIComponent(language)}`
    : baseRoute;
};

export const getLmsCoursePlayerRoute = (
  courseId: string,
  language?: string
) => {
  const baseRoute = `/kaerdemy/learner/courses/${courseId}/play`;
  return language
    ? `${baseRoute}?language=${encodeURIComponent(language)}`
    : baseRoute;
};

export const getLmsScormLaunchRoute = (enrolmentId: string) =>
  `/kaerdemy/learner/enrolments/${enrolmentId}/launch`;

export const getLmsCreateCourseRoute = (mode: LmsMode) =>
  `${getLmsCoursesRoute(mode)}/create`;

export const getLmsEnrolmentsRoute = (mode: LmsMode) =>
  mode === "manager"
    ? "/kaerdemy/manager/enrolments"
    : "/kaerdemy/administrator/enrolments";

export const getLmsCompanyDetailsRoute = () =>
  "/kaerdemy/administrator/overview";

export const getLmsEnrolmentAssignmentsRoute = (mode: LmsMode) =>
  mode === "manager"
    ? "/kaerdemy/manager/enrolmentAssignments"
    : "/kaerdemy/administrator/enrolmentAssignments";

export const isLmsManagerMode = (mode: LmsMode) => mode === "manager";

export const getLmsManagerRequestConfig = (
  mode: LmsMode,
  selectedCompanySubjectId?: string | number
) =>
  mode === "manager" && selectedCompanySubjectId
    ? {
        shouldNotImpersonate: false,
      }
    : {};

export const getLmsManagerRequestParameters = (
  mode: LmsMode,
  selectedCompanySubjectId?: string | number
): Record<string, string | number> =>
  mode === "manager" && selectedCompanySubjectId
    ? { impersonate_subject: selectedCompanySubjectId }
    : {};

export interface LmsDropDownOption {
  value: string;
  label: string;
}

export const isValidLmsIntervalValue = (value?: string) =>
  !value?.trim() || (Number.isSafeInteger(Number(value)) && Number(value) >= 0);

export interface LmsCourseFormValues {
  title: string;
  description: string;
  category: LmsDropDownOption | null;
  showCourseDurationOnCertificate: boolean;
  certificateExpirable: boolean;
  isPrivate: boolean;
  completionIntervalInDays: string;
  renewalCooldownInDays: string;
  validityIntervalInDays: string;
}

export interface LmsCourseContentValues {
  title: string;
  description: string;
}

export interface LmsCourseSharedSettingsFormValues {
  isActive: boolean;
  category: LmsDropDownOption | null;
  showCourseDurationOnCertificate: boolean;
  certificateExpirable: boolean;
  isPrivate: boolean;
  completionIntervalInDays: string;
  renewalCooldownInDays: string;
  validityIntervalInDays: string;
}

export interface LmsCourseVariantFormValues {
  language: LmsDropDownOption | null;
  durationInMinutes: string;
  isActive: boolean;
}

export interface LmsCourseEditorVariantFormValues
  extends LmsCourseVariantFormValues {
  id: string;
}

export interface LmsCourseEditorFormValues
  extends LmsCourseSharedSettingsFormValues {
  variants: LmsCourseEditorVariantFormValues[];
}

export const LMS_COURSE_SHARED_FIELD_NAMES: readonly (keyof LmsCourseSharedSettingsFormValues)[] =
  [
    "isActive",
    "category",
    "showCourseDurationOnCertificate",
    "certificateExpirable",
    "isPrivate",
    "completionIntervalInDays",
    "renewalCooldownInDays",
    "validityIntervalInDays",
  ];

export const LMS_COURSE_VARIANT_EDITABLE_FIELD_NAMES: readonly (keyof Pick<
  LmsCourseVariantFormValues,
  "durationInMinutes" | "isActive"
>)[] = ["durationInMinutes", "isActive"];

export interface LmsCourseLanguageVariant {
  id?: string;
  course?: string;
  language?: string;
  duration_in_minutes?: number | null;
  basename_of_featured_image?: string;
  basename_of_scorm_archive?: string;
  featured_image_url?: string | null;
  featured_image_display_name?: string | null;
  scorm_archive_display_name?: string | null;
  featured_image_view_link?: string | null;
  featured_image_thumbnail_view_link?: string | null;
  scorm_file_download_link?: string | null;
  scorm_file_upload_metadata?: {
    uploaded_by_name?: string | null;
    uploaded_on?: string | null;
  } | null;
  scorm_file_uploaded_by?: unknown;
  scorm_file_uploaded_on?: string | null;
  is_active?: boolean;
}

export const getLmsDisplayText = (value: unknown): string => {
  if (typeof value === "string") {
    return value;
  }
  if (typeof value === "number") {
    return String(value);
  }
  if (!value || typeof value !== "object") {
    return "";
  }

  const record = value as Record<string, unknown>;
  const preferredValue = record.de ?? record.en;
  if (preferredValue !== undefined) {
    return getLmsDisplayText(preferredValue);
  }

  for (const nestedValue of Object.values(record)) {
    const displayText = getLmsDisplayText(nestedValue);
    if (displayText) {
      return displayText;
    }
  }

  return "";
};

const mapLmsCourseCategoryToOption = (
  category: unknown
): LmsDropDownOption | null => {
  if (!category) {
    return null;
  }
  if (typeof category === "string" || typeof category === "number") {
    const value = String(category);
    return { value, label: value };
  }
  if (typeof category !== "object") {
    return null;
  }

  const record = category as Record<string, unknown>;
  const value = record.value ?? record.id;
  if (value === undefined || value === null || value === "") {
    return null;
  }

  return {
    value: String(value),
    label: getLmsDisplayText(record.label ?? record.name) || String(value),
  };
};

type LmsRequestConfig = Record<string, any> & {
  method: string;
};

export const createPromiseRequest = <TResponse = any>(
  requestConfig: LmsRequestConfig
): Promise<TResponse> =>
  new Promise<TResponse>((resolve, reject) => {
    const finalRequestConfig: LmsRequestConfig = {
      shouldNotImpersonate: true,
      ...requestConfig,
      successHandler: (response: TResponse) => resolve(response),
      failHandler: (error: any) => reject(error),
    };

    api.genericApiRequest(finalRequestConfig);
  });

export const downloadLmsCertificate = ({
  enrolmentId,
  parametersToRender = {},
}: {
  enrolmentId: string;
  parametersToRender?: Record<string, string | number>;
}) =>
  createPromiseRequest({
    method: "get",
    entity: "enrolment",
    entityId: enrolmentId,
    responseType: "blob",
    shouldNotImpersonate: true,
    parametersToRender,
    additionalRouteParts: { download_certificate: "/" },
  }).then((res: any) => {
    const contentType = res.headers["content-type"];
    const blob = new Blob([res.data], {
      type: contentType ?? "application/pdf",
    });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    const disposition = res.headers["content-disposition"];
    const match = disposition?.match(/filename="?([^"]+)"?/);
    const filename = match?.[1] ?? `certificate_${enrolmentId}.pdf`;
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  });

export const formatDuration = (durationInMinutes?: number | null) => {
  if (!durationInMinutes || durationInMinutes < 1) {
    return "0m";
  }

  if (durationInMinutes < 60) {
    return `${durationInMinutes}m`;
  }

  const hours = Math.floor(durationInMinutes / 60);
  const minutes = durationInMinutes % 60;

  return minutes ? `${hours}h ${minutes}m` : `${hours}h`;
};

export const formatDateValue = (value?: string | null, withTime = false) => {
  if (!value) {
    return "-";
  }

  return dayjs(value).format(withTime ? "DD.MM.YYYY, HH:mm" : "DD.MM.YYYY");
};

export const getDefaultCourseFormValues = (): LmsCourseFormValues => ({
  title: "",
  description: "",
  category: null,
  showCourseDurationOnCertificate: false,
  certificateExpirable: false,
  isPrivate: false,
  completionIntervalInDays: "60",
  renewalCooldownInDays: "182",
  validityIntervalInDays: "365",
});

export const getLmsCourseStatusFormOptions = (
  t: (key: string) => string
): LmsDropDownOption[] => [
  { value: "active", label: t("active") },
  { value: "inactive", label: t("inactive") },
];

export const getLmsCourseLanguageLabel = (
  language: string | null | undefined,
  t: (key: string) => string
) => {
  if (!language) {
    return "-";
  }

  const languageLabels: Record<string, string> = {
    de: "language_german",
    en: "language_english",
  };
  const normalizedLanguage = language.trim().toLowerCase();
  const translationKey = languageLabels[normalizedLanguage];

  return translationKey ? t(translationKey) : language;
};

export const getLmsCourseLanguageOptions = (
  t: (key: string) => string
): LmsDropDownOption[] => [
  { value: "de", label: getLmsCourseLanguageLabel("de", t) },
  { value: "en", label: getLmsCourseLanguageLabel("en", t) },
];

export const sortLmsCourseLanguageVariants = (
  variants: LmsCourseLanguageVariant[]
): LmsCourseLanguageVariant[] =>
  [...variants].sort((left, right) =>
    (left.language ?? "")
      .trim()
      .toLowerCase()
      .localeCompare((right.language ?? "").trim().toLowerCase())
  );

export const mapLmsCourseCategoriesToOptions = (
  categories: any[]
): LmsDropDownOption[] =>
  categories.map((category: any) => ({
    value: category.id,
    label: category.name,
  }));

interface LoadLmsCourseCategoryOptionsParams {
  shouldNotImpersonate?: boolean;
  parametersToRender?: Record<string, string | number>;
}

export const loadLmsCourseCategoryOptions = (
  params: LoadLmsCourseCategoryOptionsParams = {}
) =>
  createPromiseRequest({
    entity: "mainCategory",
    method: "get",
    shouldNotImpersonate: params.shouldNotImpersonate ?? true,
    getAllPages: true,
    parametersToRender: {
      prefer_translation: true,
      ...(params.parametersToRender ?? {}),
    },
  }).then((response: any) =>
    mapLmsCourseCategoriesToOptions(response?.data?.results ?? [])
  );

export const shouldShowLmsCourseFieldError = (
  fieldName: keyof LmsCourseFormValues,
  errors: Record<string, any>,
  touchedFields: Record<string, any>,
  isSubmitted: boolean
) =>
  Boolean(errors[fieldName]) &&
  (Boolean(touchedFields[fieldName]) || isSubmitted);

export const getLmsCourseFieldErrorMessage = (
  fieldName: keyof LmsCourseFormValues,
  errors: Record<string, any>,
  touchedFields: Record<string, any>,
  isSubmitted: boolean
) =>
  shouldShowLmsCourseFieldError(fieldName, errors, touchedFields, isSubmitted)
    ? String(errors[fieldName]?.message ?? "")
    : "";

export const shouldHideLmsCertificateValidityError = (
  fieldName: string,
  certificateExpirable: boolean
) => fieldName === "validityIntervalInDays" && !certificateExpirable;

export const hasVisibleLmsCourseFieldErrors = (
  errors: Record<string, any>,
  certificateExpirable: boolean
) =>
  Object.keys(errors).some(
    (fieldName) =>
      !shouldHideLmsCertificateValidityError(fieldName, certificateExpirable)
  );

export const areLmsCourseRequiredFieldsFilled = (
  values: LmsCourseFormValues
) => {
  const certificateExpirable = Boolean(values.certificateExpirable);

  return (
    Boolean(values.title?.trim()) &&
    Boolean(values.category) &&
    Number(values.completionIntervalInDays) > 0 &&
    (!certificateExpirable ||
      (Boolean(values.validityIntervalInDays.trim()) &&
        isValidLmsIntervalValue(values.validityIntervalInDays)))
  );
};

export const mapCourseToSharedCourseFormValues = (
  course: any
): LmsCourseSharedSettingsFormValues => ({
  isActive: course?.is_active !== false,
  category: mapLmsCourseCategoryToOption(course?.category),
  showCourseDurationOnCertificate: Boolean(
    course?.show_course_duration_on_certificate
  ),
  certificateExpirable: Boolean(course?.has_expirable_certificate),
  isPrivate: Boolean(course?.is_private),
  completionIntervalInDays: course?.completion_interval_in_days
    ? String(course.completion_interval_in_days)
    : "",
  renewalCooldownInDays: String(course?.renewal_cooldown_in_days ?? 182),
  validityIntervalInDays: !course?.has_expirable_certificate
    ? ""
    : course?.validity_interval_in_days !== undefined &&
      course?.validity_interval_in_days !== null
    ? String(course.validity_interval_in_days)
    : "",
});

const buildLmsCourseValidityPayload = (
  values: Pick<
    LmsCourseFormValues,
    "certificateExpirable" | "validityIntervalInDays"
  >
) => ({
  validity_interval_in_days:
    values.certificateExpirable && values.validityIntervalInDays.trim()
      ? Number(values.validityIntervalInDays)
      : null,
});

export const mapCourseToCourseContentValues = (
  course: any
): LmsCourseContentValues => ({
  title: getLmsDisplayText(course?.title ?? course?.name),
  description: getLmsDisplayText(course?.description),
});

export const buildLmsCourseSharedSubmitData = (
  values: LmsCourseSharedSettingsFormValues,
  content: LmsCourseContentValues,
  isActive = values.isActive
) => ({
  title: content.title.trim(),
  description: content.description || "",
  is_active: isActive,
  category: values.category?.value,
  type: "e_learning",
  show_course_duration_on_certificate: values.showCourseDurationOnCertificate,
  has_expirable_certificate: values.certificateExpirable,
  is_private: values.isPrivate,
  completion_interval_in_days: Number(values.completionIntervalInDays),
  renewal_cooldown_in_days: Number(values.renewalCooldownInDays),
  ...buildLmsCourseValidityPayload(values),
});

export const mapVariantToCourseVariantFormValues = (
  variant: LmsCourseLanguageVariant,
  t: (key: string) => string
): LmsCourseVariantFormValues => ({
  language: variant?.language
    ? {
        value: variant.language,
        label: getLmsCourseLanguageLabel(variant.language, t),
      }
    : null,
  durationInMinutes: variant?.duration_in_minutes
    ? String(variant.duration_in_minutes)
    : "",
  isActive: variant?.is_active ?? true,
});

export const mapCourseToCourseEditorFormValues = (
  course: any,
  t: (key: string) => string
): LmsCourseEditorFormValues => ({
  ...mapCourseToSharedCourseFormValues(course),
  variants: sortLmsCourseLanguageVariants(course?.variants ?? []).map(
    (variant): LmsCourseEditorVariantFormValues => ({
      id: String(variant.id ?? ""),
      ...mapVariantToCourseVariantFormValues(variant, t),
    })
  ),
});

export const isLmsCourseSharedSettingsDirty = (dirtyFields: any) =>
  LMS_COURSE_SHARED_FIELD_NAMES.some((fieldName) =>
    Boolean(dirtyFields?.[fieldName])
  );

export const getDirtyLmsCourseVariantIndexes = (dirtyFields: any): number[] =>
  (dirtyFields?.variants ?? []).reduce(
    (dirtyIndexes: number[], variantDirtyFields: any, variantIndex: number) => {
      const isVariantDirty = LMS_COURSE_VARIANT_EDITABLE_FIELD_NAMES.some(
        (fieldName) => Boolean(variantDirtyFields?.[fieldName])
      );

      if (isVariantDirty) {
        dirtyIndexes.push(variantIndex);
      }

      return dirtyIndexes;
    },
    []
  );

export const countLmsCourseEditorDirtyFields = (
  dirtyFields: any,
  {
    includeSharedFields = true,
    includeVariantFields = true,
  }: {
    includeSharedFields?: boolean;
    includeVariantFields?: boolean;
  } = {}
) => {
  const sharedFieldCount = includeSharedFields
    ? LMS_COURSE_SHARED_FIELD_NAMES.filter((fieldName) =>
        Boolean(dirtyFields?.[fieldName])
      ).length
    : 0;
  const variantFieldCount = includeVariantFields
    ? (dirtyFields?.variants ?? []).reduce(
        (count: number, variantDirtyFields: any) =>
          count +
          LMS_COURSE_VARIANT_EDITABLE_FIELD_NAMES.filter((fieldName) =>
            Boolean(variantDirtyFields?.[fieldName])
          ).length,
        0
      )
    : 0;

  return sharedFieldCount + variantFieldCount;
};

export const hasLmsCourseVariantScormPackage = (
  variant?: LmsCourseLanguageVariant | null
) =>
  Boolean(
    variant?.basename_of_scorm_archive || variant?.scorm_archive_display_name
  );

export const isLmsCourseEnrolmentEligible = (
  course?: {
    is_active?: boolean;
    variants?: LmsCourseLanguageVariant[];
  } | null
) =>
  Boolean(
    course?.is_active &&
      course.variants?.some(
        (variant) =>
          variant.is_active &&
          variant.scorm_file_uploaded_by &&
          variant.scorm_file_uploaded_on
      )
  );

export const buildLmsCourseVariantSubmitData = (
  values: LmsCourseVariantFormValues,
  courseId: string
) => ({
  course: courseId,
  language: values.language?.value,
  duration_in_minutes: Number(values.durationInMinutes),
  is_active: values.isActive,
});

export const getUnusedLmsCourseLanguageOptions = (
  languageOptions: LmsDropDownOption[],
  variants: Pick<LmsCourseLanguageVariant, "language">[],
  currentLanguage?: string
) => {
  const usedLanguages = new Set(
    variants
      .map((variant) => variant.language)
      .filter((language): language is string => Boolean(language))
  );

  return languageOptions.filter(
    (option) =>
      option.value === currentLanguage || !usedLanguages.has(option.value)
  );
};

export const buildLmsCourseSubmitData = (values: LmsCourseFormValues) => {
  const submitData: Record<string, any> = {
    title: values.title.trim(),
    description: values.description || "",
    is_active: true,
    category: values.category?.value,
    type: "e_learning",
    show_course_duration_on_certificate: values.showCourseDurationOnCertificate,
    has_expirable_certificate: values.certificateExpirable,
    is_private: values.isPrivate,
    completion_interval_in_days: Number(values.completionIntervalInDays),
    renewal_cooldown_in_days: Number(values.renewalCooldownInDays),
    ...buildLmsCourseValidityPayload(values),
  };

  return submitData;
};

export const getLmsCourseSelectedLanguage = (
  course?: any
): string | undefined => {
  const selectedLanguage = course?.selected_language;
  if (typeof selectedLanguage === "string" && selectedLanguage.trim()) {
    return selectedLanguage;
  }

  return course?.available_languages?.find(
    (language: unknown): language is string =>
      typeof language === "string" && Boolean(language.trim())
  );
};

export const getLmsCourseImageViewLink = (
  viewLink: string,
  resourceUrl?: string | null
) => resourceUrl
  ? `${viewLink}${viewLink.includes("?") ? "&" : "?"}v=${encodeURIComponent(resourceUrl)}` // view endpoints stay the same when an upload replaces the underlying image
  : viewLink;

export const getLmsCourseFeaturedImage = (course: any) => {
  const variantsWithImages = Array.isArray(course?.variants)
    ? course.variants.filter(
        (variant: LmsCourseLanguageVariant) =>
          variant.is_active !== false &&
          variant.featured_image_view_link &&
          (variant.featured_image_display_name ||
            variant.basename_of_featured_image ||
            variant.featured_image_url)
      )
    : [];
  const normalizedSelectedLanguage = getLmsCourseSelectedLanguage(course)
    ?.trim()
    .toLowerCase();
  const selectedVariant = normalizedSelectedLanguage
    ? variantsWithImages.find(
        (variant: LmsCourseLanguageVariant) =>
          variant.language?.trim().toLowerCase() === normalizedSelectedLanguage
      )
    : null;
  const hasCourseImage = Boolean(
    course?.featured_image_view_link &&
      (course?.featured_image_display_name ||
        course?.basename_of_featured_image ||
        course?.featured_image_url)
  );
  const source = selectedVariant ?? (hasCourseImage ? course : null);

  if (!source?.featured_image_view_link) {
    return null;
  }

  return {
    id: String(source.id ?? course?.id ?? ""),
    displayName:
      source.featured_image_display_name ??
      source.basename_of_featured_image ??
      `${course?.title ?? course?.name ?? "course-image"}`,
    viewLink: getLmsCourseImageViewLink(source.featured_image_view_link, source.featured_image_url),
    thumbnailViewLink: source.featured_image_thumbnail_view_link
      ? getLmsCourseImageViewLink(source.featured_image_thumbnail_view_link, source.featured_image_url)
      : source.featured_image_thumbnail_view_link,
  };
};
